// @flow
import type { FSA } from 'types/redux';
import type { Cohorts } from 'types/dashboard';
import {
  COHORT_CREATE,
  COHORT_GET,
  COHORT_DELETE,
  UNSET_COHORT,
  COHORT_PASSENGER_ASSOCIATE,
  COHORT_DRIVER_ASSOCIATE,
  GET_COHORTS,
  GET_COHORT_STATUS,
  DOWNLOAD_COHORT_YAML,
} from 'actions/cohorts';
import {
  PASSENGER_DELETE,
  PASSENGER_BOOKING_CREATE,
  PASSENGER_BOOKING_DELETE,
  PASSENGER_GET_NEARBY_TAXI_TYPES,
} from 'actions/passengers';
import {
  DRIVER_DELETE,
  DRIVER_BOOKING_DROP_OFF,
} from 'actions/drivers';
import {
  SHOW_PASSENGERS_MODAL,
  SHOW_PASSENGER_EDIT_MODAL,
  SHOW_DRIVERS_MODAL,
  SHOW_DRIVER_EDIT_MODAL,
  SHOW_DRIVER_LOCATION_MODAL,
  SHOW_DRIVER_FARE_MODAL,
  CLOSE_DRIVER_FARE_MODAL,
  DRIVER_DRAG,
  UNSET_NEWLY_CREATED_BOOKING,
  SET_ACTIVE_BOOKING,
  UNSET_ACTIVE_BOOKING,
  LOGIN,
  LOGOUT,
  LOCAL_LOGOUT,
  CONCEDO_LOGIN,
  CONCEDO_CHECK,
  VALIDATE_TOKEN,
  REVERSE_GEO,
  FORWARD_GEO,
  START_SIMULATION,
  SIMULATION_GET_REPORT,
  RESOURCE_CREATION_ERROR,
  SIMULATION_TAXI_TYPES,
  GET_TOKEN_LIST,
  ADD_NEW_TOKEN,
  ACTIVATE_TOKEN,
  DEACTIVATE_TOKEN,
  DELETE_TOKEN,
  SIMULATION_DASHBOARD,
  GET_SIMULATION_REPORT,
  TOGGLE_SIMULATION_STATUS,
  RESET_SIMULATION_DASHBOARD,
  GENERATE_RESOURCES, RESET_RESULTANT_COHORTS,
} from 'actions/app';

import * as RequestResultCases from 'middlewares/api-middleware';
import { Map, List } from 'immutable';
import _ from 'lodash';

export type AppState = {
  activeCohortID: ?string,
  showPassengersModal: boolean,
  passengerBeingEdited: ?string,
  showDriversModal: boolean,
  driverCompletingBooking: ?{
    driverID: string,
    bookingID: string,
  },
  driverBeingEdited: ?string,
  driverLocationBeingEdited: ?string,
  driverBeingDragged: ?string,
  newlyCreatedBookingID?: ?string,
  activeBooking: ?{
    passengerID: ?string,
    driverID: ?string,
    bookingID: string,
  },
  user: ?{
    accessToken?: string,
    concedoToken?: string,
    cohorts?: Cohorts,
    lastOffset?: ?string,
  },
  nearbyTaxiTypes: ?Array<any>,
  pois: Map,
  simulationList: ?Array<any>,
  resultantCohortIDs: ?Array<any>,
  cohortStatus: ?Array<any>,
  cohortYamlFile: ?string,
};

export const defaultAppState: AppState = {
  activeCohortID: null,
  showPassengersModal: false,
  passengerBeingEdited: null,
  showDriversModal: false,
  driverCompletingBooking: null,
  driverBeingEdited: null,
  driverLocationBeingEdited: null,
  driverBeingDragged: null,
  newlyCreatedBookingID: null,
  activeBooking: null,
  user: null,
  nearbyTaxiTypes: null,
  pois: null,
  errors: null,
  simulationList: null,
  resultantCohortIDs: null,
  cohortStatus: null,
  cohortYamlFile: null,
};

function app(state: AppState = defaultAppState, action: FSA): AppState {
  const payload = action.payload;
  const response = payload ? payload.response : null;
  if (payload && response && response.status === 401) {
    return {
      ...state,
      user: null,
    };
  }
  switch (action.type) {
    case '@@router/LOCATION_CHANGE':
      {
        const re = /cohorts\/(\d+)/;
        const matches = action.payload.pathname.match(re);
        if (matches && matches[1]) {
          const cohortID: string = matches[1];
          return {
            ...state,
            activeCohortID: cohortID,
            activeBooking: null,
          };
        }
        return state;
      }
    case COHORT_CREATE + RequestResultCases.SUCCESS:
      return {
        ...state,
        activeCohortID: action.payload.cohortID,
        activeBooking: null,
      };
    case COHORT_GET + RequestResultCases.SUCCESS:
      return {
        ...state,
        activeCohortID: action.meta && action.meta.cohortID,
        activeBooking: null,
      };
    case COHORT_DELETE:
      if (state.activeCohortID === action.payload.cohortID) {
        return {
          ...state,
          activeCohortID: null,
          activeBooking: null,
        };
      }
      return state;
    // Directly remove non-existing cohorts to handle the case of fetching cohorts
    // that existed before a server restart.
    // TODO: When we persist cohorts data this can be removed.
    case COHORT_GET + RequestResultCases.FAILURE:
    case COHORT_DELETE + RequestResultCases.SUCCESS:
      if (action.meta && state.activeCohortID === action.meta.cohortID) {
        return {
          ...state,
          activeCohortID: null,
          activeBooking: null,
        };
      }
      return state;
    case UNSET_COHORT:
      return {
        ...state,
        activeCohortID: null,
        activeBooking: null,
      };
    case SHOW_PASSENGERS_MODAL:
      return {
        ...state,
        showPassengersModal: action.payload,
      };
    case SHOW_PASSENGER_EDIT_MODAL:
      return {
        ...state,
        passengerBeingEdited: action.payload,
      };
    case COHORT_PASSENGER_ASSOCIATE + RequestResultCases.SUCCESS:
      return {
        ...state,
        showPassengersModal: false,
      };
    case SHOW_DRIVERS_MODAL:
      return {
        ...state,
        showDriversModal: action.payload,
      };
    case SHOW_DRIVER_EDIT_MODAL:
      return {
        ...state,
        driverBeingEdited: action.payload,
      };
    case SHOW_DRIVER_LOCATION_MODAL:
      return {
        ...state,
        driverLocationBeingEdited: action.payload,
      };
    case SHOW_DRIVER_FARE_MODAL:
      return {
        ...state,
        driverCompletingBooking: action.payload,
      };
    case CLOSE_DRIVER_FARE_MODAL:
      return {
        ...state,
        driverCompletingBooking: null,
      };
    case DRIVER_BOOKING_DROP_OFF + RequestResultCases.SUCCESS:
      if (!!state.driverCompletingBooking &&
        (action.meta && action.meta.driverID === state.driverCompletingBooking.driverID) &&
        (action.meta && action.meta.bookingID === state.driverCompletingBooking.bookingID)) {
        return {
          ...state,
          driverCompletingBooking: null,
        };
      }
      return state;
    case DRIVER_DRAG:
      return {
        ...state,
        driverBeingDragged: action.payload,
      };
    case COHORT_DRIVER_ASSOCIATE + RequestResultCases.SUCCESS:
      return {
        ...state,
        showDriversModal: false,
      };
    case PASSENGER_BOOKING_CREATE + RequestResultCases.SUCCESS:
      return {
        ...state,
        newlyCreatedBookingID: action.payload.bookingID,
      };
    case PASSENGER_BOOKING_CREATE + RequestResultCases.FAILURE:
    case UNSET_NEWLY_CREATED_BOOKING:
      return {
        ...state,
        newlyCreatedBookingID: null,
      };
    case SET_ACTIVE_BOOKING:
      return {
        ...state,
        activeBooking: {
          passengerID: action.payload.passengerID,
          driverID: action.payload.driverID,
          bookingID: action.payload.bookingID,
        },
      };
    case UNSET_ACTIVE_BOOKING:
      return {
        ...state,
        activeBooking: null,
      };
    case PASSENGER_DELETE:
    case PASSENGER_DELETE + RequestResultCases.SUCCESS:
      // Unset activeBooking if passenger for activeBooking is deleted.
      if (state.activeBooking &&
        ((action.meta && action.meta.passengerID === state.activeBooking.passengerID) ||
          (action.payload && action.payload.passengerID === state.activeBooking.passengerID))) {
        return {
          ...state,
          activeBooking: null,
        };
      }
      return state;
    case DRIVER_DELETE:
    case DRIVER_DELETE + RequestResultCases.SUCCESS:
      // Unset activeBooking if driver for activeBooking is deleted.
      if (state.activeBooking &&
        ((action.meta && action.meta.driverID === state.activeBooking.driverID) ||
          (action.payload && action.payload.driverID === state.activeBooking.driverID))) {
        return {
          ...state,
          activeBooking: null,
        };
      }
      return state;
    case PASSENGER_BOOKING_DELETE:
      if (state.activeBooking && action.payload.bookingID === state.activeBooking.bookingID) {
        return {
          ...state,
          activeBooking: null,
        };
      }
      return state;
    case LOGIN + RequestResultCases.SUCCESS:
    case VALIDATE_TOKEN + RequestResultCases.SUCCESS:
      return action.payload.jwtToken ? {
        ...state,
        user: {
          accessToken: action.payload.jwtToken,
        },
      } : state;
    case CONCEDO_LOGIN + RequestResultCases.SUCCESS:
      return {
        ...state,
        user: {
          concedoToken: action.payload.tokens.concedoToken.access_token,
          accessToken: action.payload.tokens.internalToken,
        },
      };
    case CONCEDO_CHECK + RequestResultCases.FAILURE:
      return action.payload.valid ? state : {
        ...state,
        user: null,
      };
    case LOGOUT + RequestResultCases.SUCCESS:
    case LOGIN + RequestResultCases.FAILURE:
    case VALIDATE_TOKEN + RequestResultCases.FAILURE:
    case LOCAL_LOGOUT:
      return {
        ...state,
        user: null,
      };
    case PASSENGER_GET_NEARBY_TAXI_TYPES + RequestResultCases.SUCCESS:
      return {
        ...state,
        nearbyTaxiTypes: action.payload.taxiTypes,
      };
    case PASSENGER_GET_NEARBY_TAXI_TYPES + RequestResultCases.FAILURE:
      return {
        ...state,
        nearbyTaxiTypes: null,
      };
    case REVERSE_GEO + RequestResultCases.SUCCESS:
      {
        const { meta } = action;
        if (meta && meta.params.query) {
          return {
            ...state,
            pois: (state.pois || Map()).set(List(meta.params.query), payload.body.features[0].place_name),
          };
        }

        return state;
      }
    case FORWARD_GEO + RequestResultCases.SUCCESS:
      {
        const { meta } = action;
        if (meta && meta.params.query) {
          return {
            ...state,
            autoComplete: payload.body.features,
          };
        }

        return state;
      }
    case START_SIMULATION + RequestResultCases.SUCCESS:
      {
        return {
          ...state,
          activeCohortID: payload.cohortID,
          simulationCompleted: false,
          totalBookings: null,
          successfulRides: null,
          successRate: null,
          allocationRate: null,
          passengerIDs: null,
          driverIDs: null,
          errors: null,
          timeoutEvent: false,
          bookings: null,
          resourceCreationError: false,
        };
      }
    case START_SIMULATION + RequestResultCases.FAILURE:
      {
        return {
          ...state,
          activeCohortID: null,
          simulationCompleted: false,
          totalBookings: null,
          successfulRides: null,
          successRate: null,
          allocationRate: null,
          passengerIDs: null,
          driverIDs: null,
          errors: payload.message,
          timeoutEvent: false,
          bookings: null,
          resourceCreationError: false,
        };
      }
    case SIMULATION_GET_REPORT:
      {
        return {
          ...state,
          totalBookings: payload.totalBookings,
          successfulRides: payload.successfulRides,
          successRate: payload.successRate,
          allocationRate: payload.allocationRate,
          passengerIDs: payload.passengerIDs,
          driverIDs: payload.driverIDs,
          errors: payload.errors,
          simulationCompleted: true,
          timeoutEvent: payload.timeoutEvent,
          bookings: payload.bookings,
          resourceCreationError: false,
        };
      }
    case RESOURCE_CREATION_ERROR:
      {
        return {
          ...state,
          resourceCreationError: true,
        };
      }
    case SIMULATION_TAXI_TYPES + RequestResultCases.SUCCESS:
      {
        return {
          ...state,
          taxiTypes: payload.taxiTypeIDs,
        };
      }
    case SIMULATION_TAXI_TYPES + RequestResultCases.FAILURE:
      {
        return {
          ...state,
          taxiTypes: [],
        };
      }
    case GET_TOKEN_LIST + RequestResultCases.SUCCESS:
      {
        return {
          ...state,
          tokenList: payload.tokens,
          tokenCreationError: false,
          updateTokenSuccess: false,
          newToken: null,
        };
      }
    case ADD_NEW_TOKEN + RequestResultCases.FAILURE:
      {
        return {
          ...state,
          tokenCreationError: true,
          updateTokenSuccess: false,
          newToken: null,
        };
      }
    case ACTIVATE_TOKEN + RequestResultCases.SUCCESS:
    case DEACTIVATE_TOKEN + RequestResultCases.SUCCESS:
    case DELETE_TOKEN + RequestResultCases.SUCCESS:
      {
        return {
          ...state,
          tokenCreationError: false,
          updateTokenSuccess: true,
          newToken: null,
        };
      }
    case ADD_NEW_TOKEN + RequestResultCases.SUCCESS:
      {
        return {
          ...state,
          tokenCreationError: false,
          updateTokenSuccess: true,
          newToken: payload.jwtToken,
        };
      }
    case SIMULATION_DASHBOARD + RequestResultCases.SUCCESS:
      {
        let displaySimulations = null;
        if (state.simulationList == null) {
          displaySimulations = action.payload.simulationList;
        } else if (action.payload.simulationList != null) {
          displaySimulations = state.simulationList.concat(action.payload.simulationList);
        } else {
          displaySimulations = state.simulationList;
        }
        return {
          ...state,
          simulationList: displaySimulations,
          lastEvaluatedKey: action.payload.lastEvaluatedKey,
        };
      }
    case SIMULATION_DASHBOARD + RequestResultCases.FAILURE:
      {
        return {
          ...state,
          simulationList: [],
          lastEvaluatedKey: null,
        };
      }
    case TOGGLE_SIMULATION_STATUS + RequestResultCases.SUCCESS:
      {
        return {
          ...state,
          simulationList: action.payload.simulationList,
          lastEvaluatedKey: action.payload.lastEvaluatedKey,
        };
      }
    case TOGGLE_SIMULATION_STATUS + RequestResultCases.FAILURE:
      {
        return {
          ...state,
          simulationList: [],
          lastEvaluatedKey: null,
        };
      }
    case RESET_SIMULATION_DASHBOARD:
      {
        return {
          ...state,
          simulationList: [],
          lastEvaluatedKey: null,
        };
      }
    case GET_SIMULATION_REPORT + RequestResultCases.SUCCESS:
      {
        return {
          ...state,
          simulationDetails: action.payload.simulationDetails,
        };
      }
    case GET_SIMULATION_REPORT + RequestResultCases.FAILURE:
      {
        return {
          ...state,
          simulationDetails: null,
        };
      }
    case GENERATE_RESOURCES + RequestResultCases.SUCCESS:
      {
        return {
          ...state,
          resultantCohortIDs: [payload.cohortID],
        };
      }
    case GENERATE_RESOURCES + RequestResultCases.FAILURE:
    case RESET_RESULTANT_COHORTS:
      {
        return {
          ...state,
          resultantCohortIDs: null,
        };
      }
    case GET_COHORTS + RequestResultCases.SUCCESS:
      return action.meta && action.meta.initial ? {
        ...state,
        user: {
          ...state.user,
          cohorts: action.payload.cohorts,
          lastOffset: action.payload.lastOffset,
        },
      } : {
        ...state,
        user: {
          ...state.user,
          cohorts: _.merge(state.user ? state.user.cohorts || {} : {}, action.payload.cohorts),
          lastOffset: action.payload.lastOffset,
        },
      };
    case GET_COHORTS + RequestResultCases.FAILURE:
      return {
        ...state,
        user: {
          ...state.user,
          cohorts: {},
        },
      };
    case GET_COHORT_STATUS:
      {
        let cohorts = null;
        if (!state.cohortStatus) {
          cohorts = [action.payload];
        } else {
          cohorts = [...state.cohortStatus, action.payload];
        }
        return {
          ...state,
          cohortStatus: cohorts,
        };
      }
    case DOWNLOAD_COHORT_YAML + RequestResultCases.SUCCESS:
      return {
        ...state,
        cohortYamlFile: action.payload,
      };
    default:
      {
        return state;
      }
  }
}

export default app;



/** WEBPACK FOOTER **
 ** ./src/js/reducers/app.js
 **/